import deep from 'deep-get-set';
import { createSelector } from 'reselect';

// Actions
export const VIDEO_COMPILATION_LOAD = 'VIDEO_COMPILATION_LOAD';
export const SET_SELECTED_VIDEO_ID = 'SET_SELECTED_VIDEO_ID';
export const SET_FILTER_TAGS = 'SET_FILTER_TAGS';
export const SET_CTA_LABEL = 'SET_CTA_LABEL';
export const SET_CTA_LINK = 'SET_CTA_LINK';
export const SET_COMPILATION_SORT = 'SET_COMPILATION_SORT';
export const SET_CTA_DESCRIPTION = 'SET_CTA_DESCRIPTION';
export const SET_AUTO_INCLUDE = 'SET_AUTO_INCLUDE';
export const SET_COMPILATION_PAGE_ENABLED = 'SET_COMPILATION_PAGE_ENABLED';
export const SET_COMPILATION_PAGE_WEB_LINK = 'SET_COMPILATION_PAGE_WEB_LINK';
export const SET_COMPILATION_PAGE_MEDIA = 'SET_COMPILATION_PAGE_MEDIA';
export const SET_COMPILATION_EDIT_MEDIA = 'SET_COMPILATION_EDIT_MEDIA';
export const SET_COMPILATION_LOADING = 'SET_COMPILATION_LOADING';
export const SET_COMPILATION_AUTO_PLAY = 'SET_COMPILATION_AUTO_PLAY';

// Initial State
const initialState = {
  id: '',
  title: '',
  autoInclude: true,
  ctaLabel: 'Submit Your Own Video!',
  ctaLink: '',
  ctaDescription: null,
  enabled: true,
  linkClicks: 0,
  pageLink: '',
  sort: 'POPULAR',
  createdAt: '',
  updatedAt: '',
  deleted: false,
  campaignId: 176,
  lastReceived: '',
  websiteLink: '',
  campaignName: '',
  campaignBranding: {},
  media: [],
  selectedMediaId: '',
  editingMedia: {},
  allTags: [],
  filterTags: [],
  isLoading: false,
  autoPlay: true
};

// Reducers
export function videoCompilationPageReducer(state = initialState, action) {
  switch (action.type) {
    case VIDEO_COMPILATION_LOAD: {
      const newState = {
        ...state,
        ...action.payload
      };
      return newState;
    }

    case SET_SELECTED_VIDEO_ID: {
      const newState = {
        ...state,
        selectedMediaId: action.mediaId
      };
      return newState;
    }

    case SET_FILTER_TAGS: {
      const newState = {
        ...state,
        filterTags: [...action.tagIds]
      };
      return newState;
    }

    case SET_CTA_LABEL: {
      const newState = {
        ...state,
        ctaLabel: action.label
      };
      return newState;
    }

    case SET_CTA_LINK: {
      const newState = {
        ...state,
        ctaLink: action.link
      };
      return newState;
    }

    case SET_CTA_DESCRIPTION: {
      const newState = {
        ...state,
        ctaDescription: action.desc
      };
      return newState;
    }

    case SET_COMPILATION_SORT: {
      const newState = {
        ...state,
        sort: action.sort
      };
      return newState;
    }

    case SET_AUTO_INCLUDE: {
      const newState = {
        ...state,
        autoInclude: action.value
      };

      return newState;
    }

    case SET_COMPILATION_PAGE_ENABLED: {
      const newState = {
        ...state,
        enabled: action.value
      };

      return newState;
    }

    case SET_COMPILATION_PAGE_WEB_LINK: {
      const newState = {
        ...state,
        websiteLink: action.link
      };

      return newState;
    }

    case SET_COMPILATION_EDIT_MEDIA: {
      const newState = {
        ...state,
        editingMedia: action.media
      };

      return newState;
    }

    case SET_COMPILATION_LOADING: {
      const newState = {
        ...state,
        isLoading: action.value
      };

      return newState;
    }

    case SET_COMPILATION_AUTO_PLAY: {
      const newState = {
        ...state,
        autoPlay: action.value
      };

      return newState;
    }

    default: {
      return state;
    }
  }
}

// Action Creators
export function loadVideoCompilationPageData(payload) {
  return {
    type: VIDEO_COMPILATION_LOAD,
    payload
  };
}

export function setSelectedMediaId(mediaId) {
  return {
    type: SET_SELECTED_VIDEO_ID,
    mediaId
  };
}

export function setFilterTags(tagIds) {
  return {
    type: SET_FILTER_TAGS,
    tagIds
  };
}

export function setCompilationCtaLabel(label) {
  return {
    type: SET_CTA_LABEL,
    label
  };
}

export function setCompilationCtaLink(link) {
  return {
    type: SET_CTA_LINK,
    link
  };
}

export function setCompilationSort(sort) {
  return {
    type: SET_COMPILATION_SORT,
    sort
  };
}

export function setCompilationCtaDescription(desc) {
  return {
    type: SET_CTA_DESCRIPTION,
    desc
  };
}

export function setCompilationPageAutoInclude(value) {
  return {
    type: SET_AUTO_INCLUDE,
    value
  };
}

export function setCompilationPageEnabled(value) {
  return {
    type: SET_COMPILATION_PAGE_ENABLED,
    value
  };
}

export function setCompilationPageWebsiteLink(link) {
  return {
    type: SET_COMPILATION_PAGE_WEB_LINK,
    link
  };
}

export function setCompilationPageEditingMedia(media) {
  return {
    type: SET_COMPILATION_EDIT_MEDIA,
    media
  };
}

export function setCompilationPageLoading(value) {
  return {
    type: SET_COMPILATION_LOADING,
    value
  };
}

export function setCompilationPageAutoPlay(value) {
  return {
    type: SET_COMPILATION_AUTO_PLAY,
    value
  };
}

// Selectors
export const getVideoCompilationState = createSelector(
  (state) => state,
  (state) => state.get('videoCompilation')
);

export const getVideoCompilationPageLoading = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => videoCompilationState?.isLoading
);

export const getVideoCompilationId = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => videoCompilationState?.id
);

export const getVideoCompilationAutoPlay = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => videoCompilationState?.autoPlay
);

export const getVideoCompilationBranding = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'campaignBranding')
);

export const getVideoCompilationMedia = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'media')
);

export const getVideoCompilationHasMedia = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => !!deep(videoCompilationState, 'media')?.length
);

export const getSelectedVideoId = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'selectedMediaId')
);

export const getVideoCompilationTags = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'allTags')
);

export const getVideoCompilationFilterTags = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'filterTags')
);

export const getVideoCompilationPageLink = createSelector(
  getVideoCompilationState,
  (videoCompilationState) => deep(videoCompilationState, 'pageLink')
);

export const getVideoCompilationPageTitle = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'title')
);

export const getVideoCompilationPageCtaLink = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'ctaLink')
);

export const getVideoCompilationPageCtaLabel = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'ctaLabel')
);

export const getVideoCompilationPageSort = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'sort')
);

export const getVideoCompilationPageAutoInclude = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'autoInclude')
);

export const getVideoCompilationPageEnabled = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'enabled')
);

export const getVideoCompilationPageCtaDescription = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'ctaDescription')
);

export const getVideoCompilationPageCampaignName = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'campaignName')
);

export const getVideoCompilationPageWebsiteLink = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'websiteLink')
);

export const getVideoCompilationSelectedEditMedia = createSelector(
  getVideoCompilationState,
  (getVideoCompilationState) => deep(getVideoCompilationState, 'editingMedia')
);

export const getVideoCompilationPageBrandingLogo = createSelector(
  getVideoCompilationBranding,
  (videoCompilationBranding) => deep(videoCompilationBranding, 'logo') || ''
);

export const getVideoCompilationPageBrandingBackgroundColor = createSelector(
  getVideoCompilationBranding,
  (videoCompilationBranding) => deep(videoCompilationBranding, 'banner') || 'rgb(33 120 246 / 1)'
);

export const getVideoCompilationPageBrandingColor = createSelector(
  getVideoCompilationBranding,
  (videoCompilationBranding) => {
    const banner = deep(videoCompilationBranding, 'banner');
    if (!banner) return 'rgb(255 255 255 / 1)';

    return deep(videoCompilationBranding, 'fontColor') || 'rgb(255 255 255 / 1)';
  }
);

export const getNextMediaId = createSelector(
  [getVideoCompilationMedia, getSelectedVideoId],
  (mediaArray, selectedMediaId) => {
    if (mediaArray && selectedMediaId) {
      const currentIndex = mediaArray.findIndex((media) => media.id === selectedMediaId);

      // if there's a next media in the array, return its id, otherwise return false
      if (currentIndex !== -1 && currentIndex < mediaArray.length - 1) {
        return mediaArray[currentIndex + 1].id;
      }
    }

    return false;
  }
);



// WEBPACK FOOTER //
// ./src/redux/modules/videoCompilationPage.js